import React, { useState, useEffect } from 'react';
import {
  Users,
  Search,
  Filter,
  Plus,
  Edit,
  Trash2,
  Download,
  Calendar,
  Award,
  ExternalLink,
  ChevronRight,
  RefreshCw,
  Mail,
  User,
  FileDown
} from 'lucide-react';
import { adminApi } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { generateResumePDF } from '../../lib/ResumeGenerator';
import { useModal } from '../ui/ModalProvider';

const InternshipManagement = () => {
  const { showConfirm } = useModal();
  const [internships, setInternships] = useState([]);
  const [filteredInternships, setFilteredInternships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedInternship, setSelectedInternship] = useState(null);
  const [formData, setFormData] = useState({
    certificateId: '',
    name: '',
    email: '',
    role: '',
    startDate: '',
    endDate: '',
    issueDate: '',
    skills: '',
    status: 'completed',
    studentDetails: ''
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadInternships();
  }, []);

  useEffect(() => {
    filterInternships();
  }, [internships, searchTerm, statusFilter]);

  const loadInternships = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getInternships();
      setInternships(response.data);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load internships",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const filterInternships = () => {
    let filtered = internships;
    if (searchTerm) {
      filtered = filtered.filter(it =>
        it.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        it.certificateId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        it.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    if (statusFilter) {
      filtered = filtered.filter(it => it.status === statusFilter);
    }
    setFilteredInternships(filtered);
  };

  const resetForm = () => {
    setFormData({
      certificateId: '',
      name: '',
      email: '',
      role: '',
      startDate: '',
      endDate: '',
      issueDate: new Date().toISOString().split('T')[0],
      skills: '',
      status: 'completed',
      studentDetails: ''
    });
    setSelectedInternship(null);
  };

  const handleEdit = (internship) => {
    setSelectedInternship(internship);
    setFormData({
      ...internship,
      startDate: new Date(internship.startDate).toISOString().split('T')[0],
      endDate: new Date(internship.endDate).toISOString().split('T')[0],
      issueDate: new Date(internship.issueDate).toISOString().split('T')[0],
      skills: Array.isArray(internship.skills) ? internship.skills.join(', ') : ''
    });
    setShowModal(true);
  };

  const handleDelete = (id) => {
    showConfirm('Are you sure you want to delete this internship?', async () => {
      try {
        await adminApi.deleteInternship(id);
        setInternships(prev => prev.filter(it => it.id !== id));
        toast({ title: "Success", description: "Internship deleted successfully" });
      } catch (error) {
        toast({ title: "Error", description: "Failed to delete internship", variant: "destructive" });
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      const payload = {
        ...formData,
        skills: formData.skills.split(',').map(s => s.trim()).filter(s => s)
      };

      if (selectedInternship) {
        await adminApi.updateInternship(selectedInternship.id, payload);
        toast({ title: "Success", description: "Internship updated successfully" });
      } else {
        await adminApi.createInternship(payload);
        toast({ title: "Success", description: "Internship created successfully" });
      }
      
      loadInternships();
      setShowModal(false);
      resetForm();
    } catch (error) {
      toast({ title: "Error", description: "Failed to save internship", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-silver mb-2">Internship Management</h1>
          <p className="text-gray-400">Manage intern records and certificates</p>
        </div>
        <div className="flex gap-3 mt-4 sm:mt-0">
          <button
            onClick={loadInternships}
            className="flex items-center gap-2 px-4 py-2 bg-gray-800 text-silver rounded-lg hover:bg-gray-700 transition"
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            onClick={() => { resetForm(); setShowModal(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            <Plus size={16} />
            Add Intern
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="glass-morphism rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, ID or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-silver focus:border-silver outline-none"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 bg-black/50 border border-gray-600 rounded-lg text-silver focus:border-silver outline-none"
          >
            <option value="">All Statuses</option>
            <option value="completed">Completed</option>
            <option value="active">Active</option>
            <option value="terminated">Terminated</option>
          </select>
          <div className="flex items-center text-gray-400">
            <Filter size={16} className="mr-2" />
            Showing {filteredInternships.length} interns
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="glass-morphism rounded-xl overflow-hidden">
        <table className="w-full text-left">
          <thead className="bg-gray-800/50 border-b border-gray-700">
            <tr>
              <th className="px-6 py-4 text-gray-300 font-medium">Intern</th>
              <th className="px-6 py-4 text-gray-300 font-medium">Certificate ID</th>
              <th className="px-6 py-4 text-gray-300 font-medium">Role</th>
              <th className="px-6 py-4 text-gray-300 font-medium">Status</th>
              <th className="px-6 py-4 text-gray-300 font-medium">Issue Date</th>
              <th className="px-6 py-4 text-gray-300 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {filteredInternships.map((it) => (
              <tr key={it.id} className="hover:bg-gray-800/30 transition">
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-900/50 flex items-center justify-center text-blue-400">
                      <User size={16} />
                    </div>
                    <div>
                      <div className="text-silver font-medium">{it.name}</div>
                      <div className="text-gray-400 text-xs">{it.email}</div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-gray-300 font-mono text-sm">{it.certificateId}</td>
                <td className="px-6 py-4 text-gray-300">{it.role}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                    it.status === 'completed' ? 'bg-green-900/30 text-green-400 border border-green-800/50' :
                    it.status === 'active' ? 'bg-blue-900/30 text-blue-400 border border-blue-800/50' :
                    'bg-red-900/30 text-red-400 border border-red-800/50'
                  }`}>
                    {it.status}
                  </span>
                </td>
                <td className="px-6 py-4 text-gray-400 text-sm">
                  {new Date(it.issueDate).toLocaleDateString()}
                </td>
                <td className="px-6 py-4 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button 
                      onClick={() => generateResumePDF(it)}
                      className="p-2 text-gray-400 hover:text-green-400 transition" title="Download Resume"
                    >
                      <FileDown size={16} />
                    </button>
                    <button 
                      onClick={() => window.open(`/internship-certificates/${it.certificateId}`, '_blank')}
                      className="p-2 text-gray-400 hover:text-blue-400 transition" title="View Certificate"
                    >
                      <ExternalLink size={16} />
                    </button>
                    <button onClick={() => handleEdit(it)} className="p-2 text-gray-400 hover:text-silver transition" title="Edit">
                      <Edit size={16} />
                    </button>
                    <button onClick={() => handleDelete(it.id)} className="p-2 text-gray-400 hover:text-red-400 transition" title="Delete">
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredInternships.length === 0 && (
          <div className="py-12 text-center text-gray-500">No internships found matching your search.</div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
          <div className="bg-[#1e1f22] border border-gray-700 rounded-2xl w-full max-w-2xl overflow-hidden shadow-2xl">
            <form onSubmit={handleSubmit}>
              <div className="px-6 py-4 border-b border-gray-700 flex items-center justify-between">
                <h2 className="text-xl font-bold text-silver">
                  {selectedInternship ? 'Edit Intern Record' : 'Add New Intern'}
                </h2>
                <button type="button" onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                  <ChevronRight size={24} className="rotate-90" />
                </button>
              </div>
              
              <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto custom-scrollbar">
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-400 uppercase">Certificate ID</label>
                  <input
                    required
                    placeholder="e.g. HXY-INT-2026-001"
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2 text-silver outline-none focus:border-blue-500"
                    value={formData.certificateId}
                    onChange={e => setFormData({...formData, certificateId: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">Full Name</label>
                  <input
                    required
                    placeholder="Intern Name"
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2 text-silver outline-none focus:border-blue-500"
                    value={formData.name}
                    onChange={e => setFormData({...formData, name: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">Email</label>
                  <input
                    required
                    type="email"
                    placeholder="Email Address"
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2 text-silver outline-none focus:border-blue-500"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">Role</label>
                  <input
                    required
                    placeholder="e.g. Fullstack Developer"
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2 text-silver outline-none focus:border-blue-500"
                    value={formData.role}
                    onChange={e => setFormData({...formData, role: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">Status</label>
                  <select
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2 text-silver outline-none focus:border-blue-500"
                    value={formData.status}
                    onChange={e => setFormData({...formData, status: e.target.value})}
                  >
                    <option value="completed">Completed</option>
                    <option value="active">Active</option>
                    <option value="terminated">Terminated</option>
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">Start Date</label>
                  <input
                    required
                    type="date"
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2 text-silver outline-none focus:border-blue-500"
                    value={formData.startDate}
                    onChange={e => setFormData({...formData, startDate: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">End Date</label>
                  <input
                    required
                    type="date"
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2 text-silver outline-none focus:border-blue-500"
                    value={formData.endDate}
                    onChange={e => setFormData({...formData, endDate: e.target.value})}
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs font-bold text-gray-400 uppercase">Issue Date</label>
                  <input
                    required
                    type="date"
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2 text-silver outline-none focus:border-blue-500"
                    value={formData.issueDate}
                    onChange={e => setFormData({...formData, issueDate: e.target.value})}
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <label className="text-xs font-bold text-gray-400 uppercase">Skills (Comma separated)</label>
                  <textarea
                    rows={2}
                    placeholder="React, Tailwind, Node.js..."
                    className="w-full bg-black/30 border border-gray-700 rounded-lg px-4 py-2 text-silver outline-none focus:border-blue-500"
                    value={formData.skills}
                    onChange={e => setFormData({...formData, skills: e.target.value})}
                  />
                </div>
              </div>

              <div className="px-6 py-4 bg-black/20 border-t border-gray-700 flex justify-end gap-3">
                <button 
                  type="button" 
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white transition"
                >
                  Cancel
                </button>
                <button 
                  disabled={saving}
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700 transition flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {selectedInternship ? 'Update Intern' : 'Save Intern'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Simple loader helper
const Loader2 = ({ className }) => (
  <RefreshCw className={`animate-spin ${className}`} />
);

export default InternshipManagement;
